import _ from 'lodash';
import { useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import {
  getFormFieldProps,
  getFormFieldWrapperProps,
  getValidationFunction,
} from '@/formbuilder/formbuilder.utilities';
import ValueWithUnits from '@/trend/ValueWithUnits.atom';
import { Icon } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { DurationTimeUnit } from '@/main/app.constants';
import type { ValueWithUnitsInput, ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export interface ValueWithUnitsProps extends Omit<ValidatingFormComponent<ValueWithUnitsItem | undefined>, 'value'> {
  component: 'ValueWithUnitsFormComponent';
  value?: ValueWithUnitsInput;
  min: number;
  propName?: string;
  minIsExclusive?: boolean;
  required?: boolean;
  availableUnits?: DurationTimeUnit[];
  insideModal?: boolean;
  disabled?: boolean;
  label?: string;
  step?: number | string;
  inlineLabel?: string;
  warningMessage?: string;
  includeAutoCheckbox?: boolean;
  autoCheckboxValue?: boolean;
  largeValueInput?: boolean;
  fullWidth?: boolean;
  autoCheckboxOnChange?: (value: boolean) => void;
  autoCheckboxId?: string;
  includeRemoveButton?: boolean;
  removeIconOnClick?: () => void;
  tooltip?: string;
}

export const ValueWithUnitsFormComponent = (props: ValueWithUnitsProps) => {
  const { t } = useTranslation();
  const {
    value,
    name,
    validation,
    inlineLabel,
    required = true,
    min,
    testId,
    minIsExclusive = false,
    warningMessage = '',
    extendValidation,
    includeAutoCheckbox = false,
    autoCheckboxValue = false,
    autoCheckboxOnChange = _.noop,
    autoCheckboxId = '',
    includeRemoveButton = false,
    removeIconOnClick,
  } = props;

  const defaultValidation = (valueWithUnits?: ValueWithUnitsItem) => {
    const { units, value } = valueWithUnits || {};
    const numericValue = value ?? Number.NaN;
    if (required || !_.isNil(units)) {
      if (!_.isNil(min)) {
        return !(minIsExclusive ? numericValue > min : numericValue >= min);
      } else {
        return false;
      }
    }

    return false;
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const { submitFailed } = useForm().getState();
  // useField gives meta.error/modified tied to input.onChange, avoiding showError being driven by a stale value prop
  const { input, meta } = useField<ValueWithUnitsItem | undefined>(name, {
    validate: appliedValidation,
    subscription: { error: true, modified: true, submitFailed: true, value: true },
  });

  const showError = Boolean(meta.error && (meta.modified || submitFailed));

  const { extraClassNames, ...rest } = getFormFieldProps({ submitFailed }, input, meta, props);
  return (
    <FormFieldWrapper {...getFormFieldWrapperProps(props)} showError={showError}>
      <div className="flexColumnContainer tw:items-center" id={`${testId}_valueWithUnits`}>
        {inlineLabel && <div className="tw:mr-sq-5">{t(inlineLabel)}</div>}
        <ValueWithUnits {...rest} isValid={!showError} defaultValue={value} />
        {includeAutoCheckbox && (
          <Checkbox
            id={autoCheckboxId}
            isChecked={autoCheckboxValue}
            classes="ml10"
            label="AUTO"
            onChange={autoCheckboxOnChange}
          />
        )}
        {includeRemoveButton && (
          <span data-testid="removeMaxDurationButton" onClick={removeIconOnClick}>
            <Icon icon="fa-close" tooltip={t('REMOVE')} extraClassNames="ml10 cursorPointer fa-xlg" />
          </span>
        )}
        {warningMessage && (
          <Icon
            icon="fa-exclamation-circle"
            tooltip={warningMessage}
            tooltipPlacement="top"
            large={true}
            extraClassNames="fw sq-text-warning pl10"
          />
        )}
      </div>
    </FormFieldWrapper>
  );
};
